import { createContext, useContext } from 'react';

/**
 * WHY A BLOCK CANNOT BE DELETED, supplied by the page rather than threaded down.
 *
 * Exactly `block-copy-context`'s shape, and for the same reason: a block card draws its actions
 * in one of two layouts — a vertical rail beside a collapsed card, or a horizontal row in an
 * expanded card's header — and which one appears depends on the viewport. A guard present in
 * only one of them is not a guard.
 *
 * ## Disabled, not refused
 *
 * The first build let the delete proceed to the page and showed a toast there. That is worse
 * on the one axis that matters: the armed delete is a two-step control, so the teacher armed
 * it, pressed again, and watched nothing happen. A disabled button with the reason on hover is
 * how the app already says this — see the variables panel's `blockedReason` and the spine
 * builder's pathway-blocked delete, both of which disable and explain rather than accept and
 * complain.
 *
 * The page still refuses in `handleRemoveBlock`. This is the half the teacher reads; that is
 * the half that holds when some other path reaches the same call.
 */
export type BlockDeleteGuard = (blockId: string) => string | undefined;

export const BlockDeleteGuardContext = createContext<BlockDeleteGuard | null>(null);
export const BlockDeleteGuardProvider = BlockDeleteGuardContext.Provider;

/** The reason this block cannot be deleted, or undefined when it can. */
export function useBlockDeleteBlocked(blockId: string | undefined): string | undefined {
  const guard = useContext(BlockDeleteGuardContext);
  return guard && blockId ? guard(blockId) : undefined;
}
